// Registry de schemas de ejercicio cargados al bundle. Hoy son 2 entries
// (rot_externa_banda, sentadilla_mini_conservadora); cuando llegue
// catálogo dinámico (post-MVP) este archivo es el único punto a tocar
// para reemplazar por una carga lazy desde `ejercicios.schema_path`.
//
// Factory `createEvaluator(schema)` dispatch al evaluator correspondiente
// según schema.type — backward compat con schemas legacy (sin type field,
// asumido "oscillating_angle" → ExerciseEvaluator).

import type { Evaluator, ExerciseSchema } from "./types";
import { ExerciseEvaluator } from "./ExerciseEvaluator";
import { SquatEvaluator } from "./SquatEvaluator";
import { NeckLateralFlexionEvaluator } from "./NeckLateralFlexionEvaluator";
import rotExternaBanda from "./exercises/rot_externa_banda.json";
import rotInternaBanda from "./exercises/rot_interna_banda.json";
import sentadillaMiniConservadora from "./exercises/sentadilla_mini_conservadora.json";
import movCuello from "./exercises/mov_cuello.json";

const EXERCISE_SCHEMAS: Record<string, ExerciseSchema> = {
  rot_externa_banda: rotExternaBanda as unknown as ExerciseSchema,
  rot_interna_banda: rotInternaBanda as unknown as ExerciseSchema,
  sentadilla_mini_conservadora:
    sentadillaMiniConservadora as unknown as ExerciseSchema,
  mov_cuello: movCuello as unknown as ExerciseSchema,
};

/** Sanity check al cargar cada schema del registry. Fail-fast para evitar
 *  bugs silenciosos en runtime cuando un caller futuro asuma presencia
 *  de `repDetector` sin guard.
 *
 *  - `tracking_mode="camera"` sin `repDetector` → throw. El ejercicio no
 *    puede ejecutarse sin detector. Si la intención era video_only,
 *    marcarlo explícitamente.
 *  - `tracking_mode="video_only"` con `repDetector` → warn (no fatal).
 *    El detector se ignora en runtime — ruido pero no rompe nada. */
function validateSchema(schema: ExerciseSchema): void {
  const mode = schema.tracking_mode ?? "camera"; // default retrocompat
  if (mode === "camera" && !schema.repDetector) {
    throw new Error(
      `Schema "${schema.id}" tiene tracking_mode="camera" pero no define repDetector. ` +
        `Si el ejercicio es video_only, marcalo explícitamente con tracking_mode="video_only".`,
    );
  }
  if (mode === "video_only" && schema.repDetector) {
    console.warn(
      `[exerciseSchemas] Schema "${schema.id}" tiene tracking_mode="video_only" pero define repDetector. Es ignorado en runtime.`,
    );
  }
}

// Validar todos los schemas del registry al import del módulo. Dev nota
// errores de schema al levantar la app (fail-fast) en lugar de runtime
// cuando un paciente abre la sesión.
for (const schema of Object.values(EXERCISE_SCHEMAS)) {
  validateSchema(schema);
}

/** Resuelve un schema por id de ejercicio. `undefined` si no existe en el
 *  registry — el caller decide cómo presentar el error al usuario. */
export function getExerciseSchema(
  exerciseId: string | undefined,
): ExerciseSchema | undefined {
  if (!exerciseId) return undefined;
  return EXERCISE_SCHEMAS[exerciseId];
}

/** True si el schema del ejercicio tiene `tracking_mode === "video_only"`.
 *  Lookup sync en el registry. Devuelve `false` si el ejercicio no está
 *  en el registry — degradación graceful: tratar como "camera" cuando
 *  hay drift DB ↔ bundle (caso documentado en deuda "Catálogo DUAL").
 *
 *  Sprint 3 B4a PR3 — usado en 4 lugares para mostrar form/display
 *  simplificado: PrescribirRutina (draft del kine), DetallePaciente
 *  (edit y readonly inline), HomePaciente (display al paciente). */
export function isVideoOnlyExercise(exerciseId: string | undefined): boolean {
  const schema = getExerciseSchema(exerciseId);
  if (!schema) return false;
  return schema.tracking_mode === "video_only";
}

/** Factory: instancia el evaluator correcto según schema.type. Legacy
 *  (rot_externa_banda sin type field) → ExerciseEvaluator. Squat
 *  (sentadilla_*) → SquatEvaluator. Cuello (mov_cuello, Sprint 3
 *  spec-medicion Bloque 4) → NeckLateralFlexionEvaluator. */
export function createEvaluator(schema: ExerciseSchema): Evaluator {
  if (schema.type === "squat_phases") {
    return new SquatEvaluator(schema);
  }
  if (schema.type === "neck_lateral_flexion") {
    return new NeckLateralFlexionEvaluator(schema);
  }
  // type === "oscillating_angle" o undefined (legacy) — evaluator binario
  return new ExerciseEvaluator(schema);
}
